import { FC, useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  MoreVert,
  Edit,
  Delete,
  Share,
  ContentCopy,
} from "@mui/icons-material";
import { useCollectionDialogStore } from "../CollectionDialog/stores/CollectionDialogStore";
import {
  createCollection,
  deleteCollection,
  type Collection,
  type CollectionType,
} from "../../../../apis/bacchus/collections";
import { useSnackbar } from "../../../../components/Snackbar";
import { useCollectionsContext } from "../hooks/useCollectionsContext";
import { ShareDialog } from "../ShareDialog/ShareDialog";

interface Props {
  collection: Collection;
}

export const CollectionsActionsMenu: FC<Props> = ({ collection }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { openEditMode } = useCollectionDialogStore();
  const { showSuccess, showError } = useSnackbar();
  const { loadCollections, setSearchQuery, refreshCollections } =
    useCollectionsContext();

  const canWrite = collection.permissions.can_write;

  const collectionType = collection.type.replace(
    /^rgif_|_collection$/g,
    ""
  ) as CollectionType;

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const editCollection = () => {
    openEditMode(collection);
    closeMenu();
  };

  const showDeleteDialog = () => {
    setDeleteDialogOpen(true);
    closeMenu();
  };

  const openShareDialog = () => {
    setShareDialogOpen(true);
    closeMenu();
  };

  const duplicateToMyAccount = async () => {
    closeMenu();
    try {
      await createCollection(collectionType, {
        name: collection.name,
        description: collection.description,
        blob: collection.blob,
        visibility: "private",
      });
      showSuccess("Collection duplicated to your account!");
      refreshCollections();
    } catch (error) {
      showError("Failed to duplicate collection");
      console.error(error);
    }
  };

  const deleteCollectionAndRefresh = async () => {
    try {
      await deleteCollection(collectionType, collection.id);
      showSuccess("Collection deleted successfully!");
      setSearchQuery(""); // Clear any search query
      await loadCollections();
    } catch (error) {
      showError("Failed to delete collection");
      console.error(error);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <IconButton
        data-testid={`collection-row-actions-btn-${collection.id}`}
        size="small"
        onClick={openMenu}
        sx={{ color: "text.secondary" }}
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {canWrite && (
          <MenuItem
            data-testid="collection-edit-menu-item"
            onClick={editCollection}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}

        {canWrite && (
          <MenuItem
            data-testid="collection-share-menu-item"
            onClick={openShareDialog}
          >
            <ListItemIcon>
              <Share fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
        )}

        {!canWrite && (
          <MenuItem
            data-testid="collection-duplicate-menu-item"
            onClick={duplicateToMyAccount}
          >
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>Duplicate to my account</ListItemText>
          </MenuItem>
        )}

        {canWrite && (
          <MenuItem
            data-testid="collection-delete-menu-item"
            onClick={showDeleteDialog}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {shareDialogOpen && (
        <ShareDialog
          collection={collection}
          onClose={() => setShareDialogOpen(false)}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Collection</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete "{collection.name}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="collection-delete-dialog-cancel-btn"
            onClick={cancelDelete}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            data-testid="collection-delete-confirm-btn"
            onClick={deleteCollectionAndRefresh}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
